import { Injectable } from '@angular/core';
import { IdName } from '@shared/models/id-name';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { OrderQuery } from './state/order-query';
import { OrderStore } from './state/order-store';
import { combineQueries } from '@datorama/akita';
import { CaseTypeEnum } from '@modules/order/models/case-type.enum';
import { LoggerService } from '@core/services/logger/logger.service';
import { LinkedCompany } from '@shared/models/linked-company';
import { SendToOption } from '@shared/models/send-to-option';
import type { Maybe } from '@/types/globals';

@Injectable()
export class OrderFacadeBase {
	protected static get componentName() {
		return 'OrderFacadeBase';
	}
	availableLabs$: Observable<Maybe<IdName[]>> = this.query.availableLabs$;
	minimalAvailableDueDate$: Observable<Date> = combineLatest([this.query.minimalAvailableDueDate$, this.shellQuery.isRxSent$]).pipe(
		filter(([, isRxSent]) => !isRxSent),
		map(([minimalDate]) => minimalDate)
	);
	isReadOnly$: Observable<boolean> = this.shellQuery.getReadOnlyState('order');

	constructor(
		protected store: OrderStore,
		protected query: OrderQuery,
		protected shellQuery: ShellQuery,
		protected logger: LoggerService
	) {}

	getDirectToLab() {
		return this.shellQuery.directToLab$.pipe(
			tap((directToLab = false) => {
				this.store.update({ directToLab: directToLab ?? false });
			})
		);
	}

	setDirectToLab(): Observable<boolean> {
		return combineQueries([this.shellQuery.directToLabs$, this.query.caseType$, this.query.sendTo$]).pipe(
			filter(([directToLabs, caseType]) => !!directToLabs && !!caseType),
			map(([directToLabs, caseType, sendTo]) => this.calcDirectToLab(caseType?.Id, directToLabs, sendTo)),
			filter(directToLab => directToLab !== this.query.directToLab),
			tap(directToLab => {
				this.store.update({ directToLab });
			})
		);
	}

	protected calcDirectToLab(caseTypeId: number, directToLabs: LinkedCompany[], sendTo: SendToOption | null): boolean {
		return (
			!!sendTo &&
			caseTypeId === (CaseTypeEnum.Restorative as number) &&
			directToLabs.some(directToLab => directToLab.Id === sendTo.Id)
		);
	}
}
